import { useEffect } from "react";
import type { Frame, Project, Space } from "@/api/spaces";
import { useCanvasStore } from "@/stores/canvas-store";
import { useCompactAgentDisplayStore } from "@/stores/compact-agent-display-store";
import { useCompanionStore } from "@/stores/companion-store";
import { useProjectStore } from "@/stores/project-store";
import { ingestFrames } from "@/lib/frame-ingest";

function stamp(minutesAgo: number) {
  return new Date(Date.now() - minutesAgo * 60_000).toISOString();
}

function project(id: string, name: string, color: string): Project {
  const now = stamp(0);
  return {
    id,
    name,
    description: "",
    status: "active",
    color,
    is_pinned: false,
    sort_order: 0,
    parallel_sessions_enabled: true,
    created_at: now,
    updated_at: now,
  };
}

function space(projectId: string, id: string, name: string): Space {
  const now = stamp(0);
  return {
    id,
    project: projectId,
    name,
    ticket_prefix: "DNK",
    canvas_state: {},
    color: "#30D158",
    is_pinned: false,
    is_onboarding: false,
    sort_order: 0,
    created_at: now,
    updated_at: now,
  };
}

function frame(
  id: string,
  spaceId: string,
  title: string,
  frameType: Frame["frame_type"],
  x: number,
  y: number,
): Frame {
  return {
    id,
    space: spaceId,
    frame_type: frameType,
    title,
    content: {},
    status: id === "preview-frame-brief" ? "streaming" : "done",
    position_x: x,
    position_y: y,
    width: 320,
    height: 190,
    agent_id: null,
    parent_frame: null,
    metadata: {},
    created_at: stamp(20),
    updated_at: stamp(2),
    task_ticket_number: null,
    task_id: null,
    task_title: null,
    edited_by_user_at: null,
  };
}

export function PreviewChromeDemoSeed() {
  useEffect(() => {
    const projects = [
      project("preview-project-core", "Dolcetto", "#30D158"),
      project("preview-project-growth", "Growth", "#64D2FF"),
      project("preview-project-lab", "Lab", "#FF9F0A"),
    ];
    const spacesByProject: Record<string, Space[]> = {
      "preview-project-core": [
        space("preview-project-core", "preview-space-canvas", "Canvas"),
        space("preview-project-core", "preview-space-agents", "Agents"),
        space("preview-project-core", "preview-space-release", "Release"),
      ],
      "preview-project-growth": [
        space("preview-project-growth", "preview-space-funnel", "Funnel"),
        space("preview-project-growth", "preview-space-copy", "Copy"),
      ],
      "preview-project-lab": [
        space("preview-project-lab", "preview-space-glass", "Glass Lab"),
      ],
    };
    const currentProject = projects[0]!;
    const currentSpace = spacesByProject[currentProject.id]![0]!;

    useProjectStore.setState({
      projects,
      spaces: spacesByProject[currentProject.id]!,
      currentProject,
      currentSpace,
      allProjectSpaces: spacesByProject,
      expandedProjectIds: new Set(projects.map((p) => p.id)),
      isLoading: false,
      error: null,
    });

    const canvas = useCanvasStore.getState();
    ingestFrames([
      frame(
        "preview-frame-brief",
        currentSpace.id,
        "Launch brief",
        "markdown",
        -80,
        -70,
      ),
      frame(
        "preview-frame-taskboard",
        currentSpace.id,
        "Taskboard",
        "taskboard",
        300,
        -40,
      ),
      frame(
        "preview-frame-research",
        currentSpace.id,
        "Research notes",
        "note",
        90,
        220,
      ),
    ]);
    canvas.selectFrame("preview-frame-brief");
    canvas.setAgentCursor({
      agentId: "preview-agent-coder",
      spaceId: currentSpace.id,
      name: "Coder",
      color: "#30D158",
      toolCall: "Editing launch brief",
      targetFrameId: "preview-frame-brief",
      lastSeen: stamp(1),
      actionSeq: 2,
      origin: "chat",
    });
    canvas.setAgentCursor({
      agentId: "preview-agent-designer",
      spaceId: currentSpace.id,
      name: "Designer",
      color: "#64D2FF",
      content: "Ready for review",
      targetFrameId: "preview-frame-taskboard",
      lastSeen: stamp(3),
      frozen: true,
      origin: "chat",
    });

    useCompactAgentDisplayStore.getState().setDockEnabled(true);
    useCompanionStore.getState().addExecution({
      id: "preview-execution",
      status: "running",
      agentName: "Coder",
      agentKey: "coder",
      spaceId: currentSpace.id,
      startedAt: stamp(6),
      todos: [
        {
          id: "preview-task-1",
          title: "Tighten native glass chrome",
          status: "running",
        },
        { id: "preview-task-2", title: "Review panel tabs", status: "pending" },
      ],
      origin: "chat",
    });
    useCompanionStore.getState().setCurrentExecution("preview-execution");
  }, []);

  return null;
}
